/**
 * Auth Store V1 - Clean Zustand Store
 * 
 * Separates concerns: Token, User, Organizations
 * Uses Zustand without persist (data fetched fresh from API)
 */

import { create } from 'zustand';
import { useCookies } from 'react-cookie';
import { getDefaultOrgId, setDefaultOrgId } from '@/utils/defaultOrg';

// ============================================================================
// TYPES
// ============================================================================

export interface MetadataTypeCount {
  __typename?: string;
  count: number;
  metadataType: string;
}

export interface LatestMetadataRetrieval {
  __typename?: string;
  id: string;
  status: string;
  progressPercentage: number;
  retrievalType: 'METADATA_TYPE' | 'FULL_ORG_REFRESH' | 'INITIAL_RETRIEVAL';
}

export interface LatestRefreshOperation {
  __typename?: string;
  id: string;
  metadataRetrievalId: string;
  status: 'QUEUED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
}

export interface MyRole {
  id: string;
  name: string;
  managementLevel: 'OWNER' | 'ADMIN' | 'MEMBER';
  capabilityKeys: string[];
}

export interface Organization {
  __typename?: string;
  id: string;
  orgName: string;
  orgType: string;
  instanceUrl: string;
  salesforceOrgId: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null;
  myRole: MyRole | null;
  latestMetadataRetrieval: LatestMetadataRetrieval | null;
  latestRefreshOperation: LatestRefreshOperation | null;
  metadataTypeCounts: MetadataTypeCount[];
}

export interface User {
  __typename?: string;
  id: string;
  email: string;
  organizations: Organization[];
}

// ============================================================================
// STORE STATE
// ============================================================================

interface AuthStoreState {
  // Token
  token: string | null;
  
  // User
  user: User | null;
  
  // Organizations
  organizations: Organization[];
  selectedOrg: Organization | null;
  
  // Loading states
  isLoadingUser: boolean;
  
  // Actions - Token
  setToken: (token: string | null) => void;
  clearToken: () => void;
  
  // Actions - User
  setUser: (user: User | null) => void;
  clearUser: () => void;
  
  // Actions - Organizations
  setOrganizations: (organizations: Organization[]) => void;
  selectOrganization: (orgId: string | null) => void;
  getSelectedOrganization: () => Organization | null;
  clearOrganizations: () => void;
  
  // Actions - Loading
  setLoadingUser: (isLoading: boolean) => void;
  
  // Actions - Clear All
  clearAll: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  token: null,
  user: null,
  organizations: [],
  selectedOrg: null,
  // Starts true — flips to false once AuthHandler's initial GetUserData fetch
  // resolves (success or error). Never set back to true afterward, so a
  // background refetch (e.g. after switching orgs) doesn't re-trigger loading UI.
  isLoadingUser: true,
};

// ============================================================================
// STORE
// ============================================================================

export const useAuthStoreV1 = create<AuthStoreState>()((set, get) => ({
  ...initialState,
  
  // ===== TOKEN ACTIONS =====
  
  setToken: (token) => {
    set({ token });
  },
  
  clearToken: () => {
    console.log('🗑️ [AuthStore] Clearing token');
    set({ token: null });
  },
  
  // ===== USER ACTIONS =====
  
  setUser: (user) => {
    set({ user });
    
    // Auto-set organizations from user data
    if (user?.organizations) {
      get().setOrganizations(user.organizations);
      
      const currentSelected = get().selectedOrg;
      if (currentSelected) {
        // Keep selectedOrg in sync with fresh org data (e.g. after user-data refetch so latestMetadataRetrieval is updated)
        const updatedOrg = user.organizations.find(org => org.id === currentSelected.id);
        if (updatedOrg) {
          set({ selectedOrg: updatedOrg });
        } else {
          // Stale org from a previous user/session — clear it and select a valid one
          set({ selectedOrg: null });
          const savedId = getDefaultOrgId();
          const savedOrgExists = savedId
            ? user.organizations.some(org => org.id === savedId)
            : false;

          if (savedOrgExists && savedId) {
            get().selectOrganization(savedId);
          } else if (user.organizations.length > 0) {
            const firstOrgId = user.organizations[0].id;
            get().selectOrganization(firstOrgId);
            setDefaultOrgId(firstOrgId);
          }
        }
      } else if (user.organizations.length > 0) {
        const savedId = getDefaultOrgId();
        const savedOrgExists = savedId
          ? user.organizations.some(org => org.id === savedId)
          : false;

        if (savedOrgExists && savedId) {
          get().selectOrganization(savedId);
        } else {
          const firstOrgId = user.organizations[0].id;
          get().selectOrganization(firstOrgId);
          setDefaultOrgId(firstOrgId);
        }
      }
    }
  },
  
  clearUser: () => {
    console.log('🗑️ [AuthStore] Clearing user');
    set({ user: null });
  },
  
  // ===== ORGANIZATION ACTIONS =====
  
  setOrganizations: (organizations) => {
    set({ organizations });
  },
  
  selectOrganization: (orgId) => {
    if (!orgId) {
      console.log('🎯 [AuthStore] Clearing selected organization');
      set({ selectedOrg: null });
      return;
    }
    
    // Find the full organization object by ID
    const { organizations } = get();
    const org = organizations.find(org => org.id === orgId);
    
    if (org) {
      set({ selectedOrg: org });
      setDefaultOrgId(org.id);
    } else {
      console.warn('⚠️ [AuthStore] Organization not found with ID:', orgId);
      set({ selectedOrg: null });
    }
  },
  
  getSelectedOrganization: () => {
    return get().selectedOrg;
  },
  
  clearOrganizations: () => {
    console.log('🗑️ [AuthStore] Clearing organizations');
    set({ organizations: [], selectedOrg: null });
  },
  
  // ===== LOADING ACTIONS =====
  
  setLoadingUser: (isLoading) => {
    set({ isLoadingUser: isLoading });
  },
  
  // ===== CLEAR ALL =====
  
  clearAll: () => {
    console.log('🧹 [AuthStore] Clearing all data');
    set(initialState);
  },
}));

// ============================================================================
// SELECTORS (for optimized re-renders)
// ============================================================================

export const authSelectorsV1 = {
  // Token
  token: () => useAuthStoreV1((state) => state.token),
  hasToken: () => useAuthStoreV1((state) => !!state.token),
  
  // User
  user: () => useAuthStoreV1((state) => state.user),
  userId: () => useAuthStoreV1((state) => state.user?.id),
  userEmail: () => useAuthStoreV1((state) => state.user?.email),
  
  // Organizations
  organizations: () => useAuthStoreV1((state) => state.organizations),
  selectedOrg: () => useAuthStoreV1((state) => state.selectedOrg),
  selectedOrgId: () => useAuthStoreV1((state) => state.selectedOrg?.id || null),
  hasOrganizations: () => useAuthStoreV1((state) => state.organizations.length > 0),
  
  // Loading
  isLoadingUser: () => useAuthStoreV1((state) => state.isLoadingUser),
};

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Hook to check if user is authenticated
 * 
 * ✅ Changed to check token only (not user)
 * This prevents redirect flash on page refresh when token exists but user hasn't loaded yet
 * 
 * ✅ Also checks cookie directly to handle new tab scenarios where store hasn't hydrated yet
 */
export const useIsAuthenticated = () => {
  const token = useAuthStoreV1((state) => state.token);
  const [cookies] = useCookies(['auth_token']);
  const cookieToken = cookies.auth_token;
  
  // Return true if either store token or cookie token exists
  // This handles the case where a new tab opens and the store hasn't hydrated yet
  return !!(token || cookieToken);
};

/**
 * Hook to get selected organization with all details
 */
export const useSelectedOrganization = () => {
  return useAuthStoreV1((state) => state.selectedOrg);
};

/**
 * Hook to get organization by ID
 */
export const useOrganizationById = (orgId: string | null) => {
  return useAuthStoreV1((state) => 
    orgId ? state.organizations.find(org => org.id === orgId) || null : null
  );
};
